import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrgNavigate } from "app/routes/useOrgNavigate";
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import {
    Container,
    Card,
    CardContent,
    Button,
    Typography,
    Alert,
    Box,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import firebaseAuth from 'Modules/firebase/firebase-auth';
import qualityIcon from '../../static/assets/icons/branding/quality_icon.svg';
import PasswordField from 'Components/PasswordField';
import { appRoutes } from 'app/routes/constants';

const getPasswordStrength = (
    password: string,
): 'Weak' | 'Medium' | 'Strong' => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score >= 4) return 'Strong';
    if (score >= 2) return 'Medium';
    return 'Weak';
};

const ResetPasswordPage: React.FC = () => {
    const [params] = useSearchParams();
    const navigate = useOrgNavigate();
    const [oobCode, setOobCode] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const code = params.get('oobCode');
        if (code) {
            setOobCode(code);
            verifyPasswordResetCode(firebaseAuth, code)
                .then(setEmail)
                .catch(() =>
                    setError('Invalid or expired password reset link.'),
                );
        } else {
            setError('Missing reset code.');
        }
    }, [params]);

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (!oobCode) {
            setError('Reset code is missing or invalid.');
            return;
        }

        try {
            await confirmPasswordReset(firebaseAuth, oobCode, newPassword);
            setSuccess(true);
            setTimeout(() => navigate(appRoutes.AUTH_LOGIN), 3000);
        } catch (err: any) {
            setError(err.message);
        }
    };

    const passwordStrength = getPasswordStrength(newPassword);
    const isLengthValid = newPassword.length >= 8;
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSymbol = /[^A-Za-z0-9]/.test(newPassword);

    return (
        <Container maxWidth="xs">
            <Card sx={{ mt: 8, p: 3 }}>
                <CardContent>
                    <Typography align="center" gutterBottom>
                        <img src={qualityIcon} height="45px" />
                    </Typography>
                    <Typography variant="h4" align="center" gutterBottom>
                        Reset Your Password
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Password reset successful! Redirecting to login...
                        </Alert>
                    )}

                    {!success && email && (
                        <Box component="form" onSubmit={handleReset}>
                            <PasswordField
                                label="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />

                            <PasswordField
                                label="Re-enter Password"
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                                error={
                                    !!confirmPassword &&
                                    newPassword !== confirmPassword
                                }
                                helperText={
                                    confirmPassword &&
                                        newPassword !== confirmPassword
                                        ? 'Passwords do not match'
                                        : ''
                                }
                            />

                            {newPassword && (
                                <>
                                    <Box sx={{ mb: 1 }}>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            Password Strength:{' '}
                                            <strong>{passwordStrength}</strong>
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                passwordStrength === 'Weak'
                                                    ? 33
                                                    : passwordStrength ===
                                                        'Medium'
                                                        ? 66
                                                        : 100
                                            }
                                            color={
                                                passwordStrength === 'Weak'
                                                    ? 'error'
                                                    : passwordStrength ===
                                                        'Medium'
                                                        ? 'warning'
                                                        : 'success'
                                            }
                                        />
                                    </Box>

                                    <List dense>
                                        <ListItem>
                                            <ListItemText
                                                primary="At least 8 characters"
                                                primaryTypographyProps={{
                                                    color: isLengthValid
                                                        ? 'success.main'
                                                        : 'text.secondary',
                                                }}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary="One uppercase letter (A-Z)"
                                                primaryTypographyProps={{
                                                    color: hasUppercase
                                                        ? 'success.main'
                                                        : 'text.secondary',
                                                }}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary="One number (0-9)"
                                                primaryTypographyProps={{
                                                    color: hasNumber
                                                        ? 'success.main'
                                                        : 'text.secondary',
                                                }}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary="One special character (e.g. !@#$%^&*)"
                                                primaryTypographyProps={{
                                                    color: hasSymbol
                                                        ? 'success.main'
                                                        : 'text.secondary',
                                                }}
                                            />
                                        </ListItem>
                                    </List>
                                </>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                sx={{ mt: 2 }}
                            >
                                Reset Password
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Container>
    );
};

export default ResetPasswordPage;
